import get from 'lodash/get';
import forEach from 'lodash/forEach';
import uuidV4 from 'uuid/v4';
import {window} from 'ssr-window';
import SentryUtils from '@spothero/utils/sentry';
import CityAPI from 'api/city';
import DestinationAPI from 'api/destination';
import EventsAPI from 'api/events';
import ExperimentUtils from 'utils/experiment';
import {initialState as destinationInitialState} from 'destination/destination-reducer';
import {initialState as eventInitialState} from 'event/event-reducer';

const loadCityData = async citySlug => {
    try {
        const {
            data: {
                data
            }
        } = await CityAPI.get(citySlug);

        return data;
    } catch (error) {
        const errorMessage = error && (error.message || error.statusText || get(error, 'response.data', ''));

        SentryUtils.addData({
            caughtError: error
        });
        SentryUtils.sendMessage({
            message: errorMessage,
            level: 'error'
        });

        throw error;
    }
};

const loadDestinationData = async destinationId => {
    try {
        const {
            data: {
                data
            }
        } = await DestinationAPI.get(destinationId);

        return data;
    } catch (error) {
        const errorMessage = error && (error.message || error.statusText || get(error, 'response.data', ''));

        SentryUtils.addData({
            caughtError: error
        });
        SentryUtils.sendMessage({
            message: errorMessage,
            level: 'error'
        });

        throw error;
    }
};

const loadEventData = async eventId => {
    try {
        const {
            data: {
                data
            }
        } = await EventsAPI.getById(eventId);

        return data;
    } catch (error) {
        const errorMessage = error && (error.message || error.statusText || get(error, 'response.data', ''));

        SentryUtils.addData({
            caughtError: error
        });
        SentryUtils.sendMessage({
            message: errorMessage,
            level: 'error'
        });

        throw error;
    }
};

const SearchRequestUtils = {
    sanitizeSearchRequestResponse({state, response}) {
        const {
            monthly: isMonthly,
            airport,
            page_info: {
                page_type: pageType,
                setup
            }
        } = response;
        const mappedData = {};
        const isSearchPage = (pageType === 'search'
            || pageType === 'city'
            || pageType === 'destination'
            || pageType === 'event');
        const isAirport = (pageType === 'destination' && setup.destination.is_airport) || airport;
        let item = null;

        forEach(state, (value, key) => {
            item = response[key];

            if (item) {
                mappedData[key] = item;
            } else {
                mappedData[key] = value;
            }
        });

        ExperimentUtils.setUserAttributes({
            monthly: Boolean(isMonthly).toString(),
        });

        return {
            ...mappedData,
            pageType,
            isSearchPage,
            id: (pageType === 'checkout' || pageType === 'facility' || isSearchPage)
                ? uuidV4()
                : null,
            airport: (!isMonthly && isAirport)
        };
    },

    async loadDataSequence({pageType, setup, citySlug, isNewCity = false}) {
        const setupData = {
            destination: destinationInitialState(),
            event: eventInitialState(),
        };

        // TODO: once these pages are getting spotId from route, can we remove this too?
        if (pageType === 'checkout' || pageType === 'facility') {
            // on checkout and airport spot detail there will be a facility in the response, pull it so proper data/rates load when the respective component renders
            setupData.spot = {
                selected: {
                    spotId: setup.facility.id
                }
            };
        }

        // if the city slug is different than what is currently saved in the store, load the new city information as well
        if (isNewCity) {
            setupData.city = {
                isPending: false,
                data: await loadCityData(citySlug),
                error: null
            };
        }

        if (pageType === 'destination' || pageType === 'event') {
            setupData.destination.data = await loadDestinationData(setup.destination.id);

            if (pageType === 'event') {
                try {
                    setupData.event.data = await loadEventData(setup.event.id);
                } catch (error) {
                    setupData.willRedirect = true;

                    // in case there are any errors loading event data, redirect the page to the destination URL
                    window.location = `/${citySlug}/${setupData.destination.data.relative_url}`;
                }
            }
        }

        return setupData;
    }
};

export default SearchRequestUtils;
